import { ActorRefFrom, assign, fromPromise, setup } from 'xstate';
import { openDeleteConfirmation, showAxiosErrorAction } from '~/features/actions';
import { FacilityFormValues } from '~/features/types';
import { onFacilityFormOpen } from '~/features/vault/facility';
import { facilityMachine } from '~/features/vault/facility/facility.machine';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import {
  createEmployerFacility,
  deleteEmployerFacility,
  getEmployerFacilities,
  patchEmployerFacility,
} from './api';
import { emptyEmployerFacilityForm } from './constants';
import { EmployerFacilityValues } from './types';
import { employerFacilityToForm, toStoredFacilityName } from './utils';

type SubmitEmployerFacilityEvent = {
  type: 'ON_SUBMIT' | 'ON_UPDATE';
  payload: { data: EmployerFacilityValues };
};

type LoadEvent = { type: 'ON_LOAD' };
type AddEvent = { type: 'ON_ADD' };
type EditEvent = { type: 'ON_EDIT'; payload: { id: number } };
type CloseModalEvent = { type: 'ON_CLOSE_MODAL' };
type DeleteEvent = { type: 'ON_DELETE'; payload: { id: number } };
type SendDeleteConfirmationEvent = {
  type: 'ON_SEND_DELETE_CONFIRMATION';
  payload: { id: number };
};
type FacilityCreatedEvent = {
  type: 'ON_FACILITY_CREATED';
  facility: FacilityFormValues;
};
type FacilityFormOpenEvent = { type: 'ON_OPEN_FACILITY_FORM' };
type FacilityFormCloseEvent = { type: 'ON_CLOSE_FACILITY_FORM' };

type Events =
  | LoadEvent
  | AddEvent
  | EditEvent
  | CloseModalEvent
  | DeleteEvent
  | SendDeleteConfirmationEvent
  | SubmitEmployerFacilityEvent
  | FacilityCreatedEvent
  | FacilityFormOpenEvent
  | FacilityFormCloseEvent;

type Context = {
  employerFacilities: EmployerFacility[];
  employerFacilityForm: EmployerFacilityValues;
  facilityRef: ActorRefFrom<typeof facilityMachine>;
  idToDelete: number;
  loading: boolean;
  modal: {
    create: boolean;
    edit: boolean;
  };
};

const closeModal = {
  modal: {
    create: false,
    edit: false,
  },
  employerFacilityForm: emptyEmployerFacilityForm,
};

const updateFacilityForm = {
  employerFacilityForm: ({ context, event }: { context: Context; event: Events }) => ({
    ...context.employerFacilityForm,
    // [MCV-1876] Store the facility's own name, not the picker label with its "(City, ST)" suffix.
    facilityName: {
      label: toStoredFacilityName((event as FacilityCreatedEvent).facility),
      value: toStoredFacilityName((event as FacilityCreatedEvent).facility),
    },
    facilityId: (event as FacilityCreatedEvent).facility.value,
  }),
};

const sortEmployerFacilities = (facilities: EmployerFacility[]) =>
  [...facilities].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.id - b.id);

const mergeEmployerFacility = (
  facilities: EmployerFacility[],
  updatedFacility: EmployerFacility
) => {
  const existingFacilityIds = new Set(facilities.map((facility) => facility.id));
  const mergedFacilities = existingFacilityIds.has(updatedFacility.id)
    ? facilities.map((facility) =>
        facility.id === updatedFacility.id
          ? updatedFacility
          : { ...facility, isPrimary: updatedFacility.isPrimary ? false : facility.isPrimary }
      )
    : [
        ...facilities.map((facility) => ({
          ...facility,
          isPrimary: updatedFacility.isPrimary ? false : facility.isPrimary,
        })),
        updatedFacility,
      ];
  return sortEmployerFacilities(mergedFacilities);
};

export const employerFacilitiesMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
  },
  actors: {
    facilityRef: facilityMachine,
    getEmployerFacilities: fromPromise(getEmployerFacilities),
    createEmployerFacility: fromPromise(createEmployerFacility),
    patchEmployerFacility: fromPromise(patchEmployerFacility),
    deleteEmployerFacility: fromPromise(deleteEmployerFacility),
  },
  actions: {
    closeModal: assign(closeModal),
    openAddModal: assign({
      modal: { create: true, edit: false },
      employerFacilityForm: emptyEmployerFacilityForm,
    }),
    editEmployerFacility: assign({
      modal: { create: false, edit: true },
      employerFacilityForm: ({ context, event }) => {
        const editEvent = event as EditEvent;
        const facility = context.employerFacilities.find(
          (item) => item.id === editEvent.payload.id
        );
        return facility ? employerFacilityToForm(facility) : emptyEmployerFacilityForm;
      },
    }),
    setFacilities: assign({
      employerFacilities: ({ event }) =>
        sortEmployerFacilities((event as unknown as { output: EmployerFacility[] }).output),
      loading: false,
    }),
    setLoading: assign({ loading: true }),
    resetLoading: assign({ loading: false }),
    updateFacilityForm: assign(updateFacilityForm),
    updateFacilitiesFromCreate: assign({
      employerFacilities: ({ context, event }) =>
        mergeEmployerFacility(
          context.employerFacilities,
          (event as unknown as { output: EmployerFacility }).output
        ),
      ...closeModal,
      loading: false,
    }),
    updateFacilitiesFromPatch: assign({
      employerFacilities: ({ context, event }) => {
        const updatedFacility = (event as unknown as { output: EmployerFacility }).output;
        return mergeEmployerFacility(context.employerFacilities, updatedFacility);
      },
      ...closeModal,
      loading: false,
    }),
    setIdToDelete: assign({
      idToDelete: ({ event }) => (event as DeleteEvent).payload.id,
      loading: true,
    }),
    deleteFacilityFromContext: assign({
      employerFacilities: ({ context }) =>
        context.employerFacilities.filter((facility) => facility.id !== context.idToDelete),
      idToDelete: -1,
      loading: false,
    }),
    sendOpenDeleteConfirmation: openDeleteConfirmation,
    sendError: showAxiosErrorAction,
  },
}).createMachine({
  context: ({ spawn }) => ({
    employerFacilities: [],
    employerFacilityForm: emptyEmployerFacilityForm,
    facilityRef: spawn('facilityRef', {
      id: 'employerFacilitiesFacilityState',
      input: undefined,
    }),
    idToDelete: -1,
    loading: false,
    modal: {
      create: false,
      edit: false,
    },
  }),
  initial: '$_VIEW',
  states: {
    $_VIEW: {
      on: {
        ON_LOAD: {
          target: '$_GET',
          actions: 'setLoading',
        },
        ON_ADD: {
          target: '$_FORM',
          actions: 'openAddModal',
        },
        ON_EDIT: {
          target: '$_FORM',
          actions: 'editEmployerFacility',
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'sendOpenDeleteConfirmation',
        },
        ON_DELETE: {
          target: '$_SEND_DELETE',
          actions: 'setIdToDelete',
        },
      },
    },
    $_GET: {
      invoke: {
        src: 'getEmployerFacilities',
        onDone: {
          target: '$_VIEW',
          actions: 'setFacilities',
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_FORM: {
      entry: ({ context }) => {
        context.facilityRef.send({ type: 'ON_CLOSE' });
      },
      on: {
        ON_CLOSE_MODAL: {
          target: '$_VIEW',
          actions: 'closeModal',
        },
        ON_SUBMIT: {
          target: '$_SEND_CREATE',
          actions: 'setLoading',
        },
        ON_UPDATE: {
          target: '$_SEND_UPDATE',
          actions: 'setLoading',
        },
        ...onFacilityFormOpen(),
      },
    },
    $_SEND_CREATE: {
      invoke: {
        src: 'createEmployerFacility',
        input: ({ event }: { event: Events }) => ({ event }),
        onDone: {
          target: '$_VIEW',
          actions: 'updateFacilitiesFromCreate',
        },
        onError: {
          target: '$_FORM',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_SEND_UPDATE: {
      invoke: {
        src: 'patchEmployerFacility',
        input: ({ event }: { event: Events }) => ({ event }),
        onDone: {
          target: '$_VIEW',
          actions: 'updateFacilitiesFromPatch',
        },
        onError: {
          target: '$_FORM',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_SEND_DELETE: {
      invoke: {
        src: 'deleteEmployerFacility',
        input: ({ event }: { event: Events }) => ({ event }),
        onDone: {
          target: '$_VIEW',
          actions: 'deleteFacilityFromContext',
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_FACILITY_VIEW: {
      on: {
        // [MCV-1836] Closing the OUTER dialog while the nested "Add New Facility" form is open
        // must unwind the whole stack to $_VIEW. Unhandled here, the event was dropped, the outer
        // dialog stayed open, and Submit could never be reached again.
        ON_CLOSE_MODAL: {
          target: '$_VIEW',
          actions: 'closeModal',
        },
        ON_CLOSE_FACILITY_FORM: {
          target: '$_FORM',
        },
        ON_FACILITY_CREATED: {
          target: '$_FORM',
          actions: 'updateFacilityForm',
        },
      },
    },
  },
});
